import type {
  Control,
  FieldErrors,
  UseFormRegister,
  UseFormRegisterReturn,
} from 'react-hook-form'
import { useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { Flex, Text, TextArea } from '@enechain/ecloud-designsystem'

import {
  FieldErrorText,
  FieldLabel,
  FieldRoot,
} from '~/components/common/Field'
import type { EmissionLinkingFieldProps } from '~/pages/SpotBidding/components/EmissionLinkingField'
import { EmissionLinkingField } from '~/pages/SpotBidding/components/EmissionLinkingField'
import type { BlockBidCreateFormInput } from '~/pages/SpotBidding/models/blockBidCreateSchema'
import { BLOCK_MEMO_FREE_MAX_LEN } from '~/pages/SpotBidding/models/blockBidCreateSchema'

type Props = {
  kind: BlockBidCreateFormInput['kind']
  control: Control<BlockBidCreateFormInput>
  register: UseFormRegister<BlockBidCreateFormInput>
  errors: FieldErrors<BlockBidCreateFormInput>
  emission: Omit<EmissionLinkingFieldProps, 'idPrefix' | 'freeTextCounter'>
  /** JEPX自由記述欄の register 結果 (呼び出し元が入力の検知を onChange に足す)。 */
  jepxFreeRegistration: UseFormRegisterReturn<'memoJepx'>
  /** 判別コメントの後ろに置ける自由記述の最大文字数 (`jepxFreeTextMaxLength`)。 */
  jepxFreeMaxLength: number
  /** 保存済みの判別コメントを会社情報の到着で分解し直す間は、 JEPX自由記述欄を入力させない。 */
  jepxFreeDisabled: boolean
}

/**
 * ブロック入札の JEPX自由記述欄 + メモ (自由記述)。
 *
 * どちらもブロック単位で 1 つ (LINK の親子・LOOP の A/B で共通) で、 保存時に両 Leg へ
 * 同じ値を載せる (spec bidding/spot-bidding.md AC-BSP-031/032、 design `spot.html` の
 * `.blk-shared-note`)。 排出係数紐付け (AC-BSP-083) も JEPX自由記述欄の見出しと本文の間に
 * 1 つだけ置く (design `#blk-emission-slot`)。 リンク・ループでは見出しの後ろに共通の範囲
 * (親+子 / 売り+買い) を出す (design `#blk-memo-scope`)。
 *
 * 入力欄は全幅の 2 行の textarea (design `#blk-memo-jepx` / `#blk-memo-free`、 個別作成の
 * [[MemoFields]] と同じ)。 旧は 1 行の `Input` で、 `FieldRoot` の `align-items: flex-start`
 * により内容幅 (実測 190px) で止まり、 70 / 100 文字の欄に 11 文字ほどしか見えなかった
 * (enechain/ebalance#11136、 2026-10-21 実測)。 `FieldRoot` を stretch にして全幅に戻す。
 */
export const BlockSharedMemoFields = ({
  kind,
  control,
  register,
  errors,
  emission,
  jepxFreeRegistration,
  jepxFreeMaxLength,
  jepxFreeDisabled,
}: Props): React.ReactElement => {
  const { t } = useTranslation()
  const memoJepx = useWatch({ control, name: 'memoJepx' }) ?? ''
  const memoFree = useWatch({ control, name: 'memoFree' }) ?? ''
  const sharedScope =
    kind === 'LINK'
      ? t('page:biddingBlock.form.memoSharedScopeLink')
      : kind === 'LOOP'
        ? t('page:biddingBlock.form.memoSharedScopeLoop')
        : undefined
  return (
    <>
      <EmissionLinkingField
        idPrefix="blockBid"
        {...emission}
        freeTextCounter={{ length: memoJepx.length, max: jepxFreeMaxLength }}
        sharedScope={sharedScope}
      />
      <FieldRoot alignItems="stretch" invalid={errors.memoJepx !== undefined}>
        <TextArea
          aria-label={t('page:biddingBlock.form.memoJepx')}
          disabled={jepxFreeDisabled}
          id="blockBid-memoJepx"
          maxLength={jepxFreeMaxLength}
          placeholder={t('page:biddingSpot.form.memoJepxPlaceholder')}
          rows={2}
          {...jepxFreeRegistration}
        />
        <FieldErrorText>{errors.memoJepx?.message}</FieldErrorText>
      </FieldRoot>

      <FieldRoot alignItems="stretch" invalid={errors.memoFree !== undefined}>
        <FieldLabel htmlFor="blockBid-memoFree">
          {t('page:biddingBlock.form.memoFree')}
        </FieldLabel>
        <TextArea
          id="blockBid-memoFree"
          maxLength={BLOCK_MEMO_FREE_MAX_LEN}
          placeholder={t('page:biddingBlock.form.memoFreePlaceholder')}
          rows={2}
          {...register('memoFree')}
        />
        <Flex
          alignItems="center"
          justifyContent="space-between"
          width="100%"
          marginTop={1}
        >
          <Text color="Text.lowEmphasis" fontSize="sm">
            {t('page:biddingBlock.form.memoFreeHint')}
          </Text>
          <Text color="Text.lowEmphasis" fontSize="sm">
            {`${memoFree.length} / ${BLOCK_MEMO_FREE_MAX_LEN}`}
          </Text>
        </Flex>
        <FieldErrorText>{errors.memoFree?.message}</FieldErrorText>
      </FieldRoot>
    </>
  )
}
